import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { Button, Property } from '../../global';

const useAssetDetailsSectionClasses = makeStyles({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'flex-start',
        flex: 1,
        margin: '-5px -10px',
        '& > *': {
            flex: '1 0 calc(100% / 3 - 20px)',
            minWidth: 200,
            margin: '5px 10px',
        },
    },
    comment: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        marginTop: 50,
        '& > div': {
            marginBottom: 16,
        },
        '& > button': {
            alignSelf: 'flex-start',
        },
    },
});

/**
 * List the main and metadata properties of the asset in store.
 *
 * @param props.metadata show all metadata information (except properties ending in `'design'`)
 * @param props.editable allow properties to be edited, if `'comment'` only the asset comment can be edited
 */
export const AssetDetails = (
    props: JSX.IntrinsicElements['div'] & { metadata?: boolean; editable?: boolean | 'comment'; onChange?: () => void },
) => {
    const { metadata = false, editable: baseEditable, onChange, ...rootProps } = props;
    const editable = baseEditable && baseEditable !== 'comment';
    const commentEditable = !!baseEditable;
    const classes = useAssetDetailsSectionClasses();
    const { t } = usePrefixTranslation('asset', { asset: 'common.AssetDetails' });
    const dispatch = useTypedDispatch();
    const { asset, version, asset_type } = useTypedSelector(state => ({
        asset: state.asset,
        version: state.asset.changes.length,
        asset_type: state.asset_type,
    }));
    const [editingComment, setEditingComment] = React.useState(false);
    const assetMetadata = Object.entries(asset.changed?.metadata ?? {}).filter(([name]) => !name.endsWith('design'));
    const needsAttention = t('needs-attention');

    React.useEffect(() => void (asset.asset && dispatch(actions.asset_type.get())), [asset.asset]);

    if (!asset.changed) return <span>{'#asset not in store'}</span>;

    return (
        <div {...rootProps} className={clsx(classes.root, rootProps.className)}>
            <Property name={'code'} value={asset.changed.code} />
            <Property
                key={`name-${version}`}
                name='name'
                value={asset.changed.name}
                error={asset.changed.name == undefined || asset.changed.name.length === 0 ? needsAttention : undefined}
                editable={editable}
                onChange={(name, value) => (dispatch(actions.asset.edit({ [name]: value })), onChange?.())}
            />
            <Property
                key={`type-${version}-${asset_type.fetching}`}
                name='type'
                value={asset.changed.type}
                options={Object.values(asset_type.items)}
                optionEquals={(a, b) => a?.id === b?.id}
                renderOption={assetType => assetType.name}
                unselect={'None'}
                error={!asset.changed.type ? needsAttention : undefined}
                editable={editable}
                onChange={(name, value) => (dispatch(actions.asset.edit({ [name]: value })), onChange?.())}
            />
            {metadata &&
                assetMetadata.map(([name, value]) => (
                    <Property
                        key={`metadata-${name}-${version}`}
                        name={name}
                        value={value}
                        error={value == undefined || value.length === 0 ? needsAttention : undefined}
                        snake
                        editable={editable}
                        onChange={(name, value) => (
                            dispatch(actions.asset.edit({ metadata: { [name]: value } })), onChange?.()
                        )}
                    />
                ))}
            {metadata &&
                assetMetadata.length % 3 !== 0 &&
                [...Array(3 - (assetMetadata.length % 3))].map((_, i) => <div key={i} />)}
            <div className={classes.comment}>
                <Property
                    name={t('comment-label')}
                    value={asset.changed.comment}
                    editable={editingComment}
                    multiline
                    rows={4}
                    onChange={(name, value) => (dispatch(actions.asset.edit({ comment: value })), onChange?.())}
                />
                <Button variant='contained' onClick={() => setEditingComment(!editingComment && commentEditable)}>
                    {t(`comment-${editingComment ? 'update' : 'edit'}`)}
                </Button>
            </div>
        </div>
    );
};
